import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faPrint, 
  faReceipt, 
  faCalculator, 
  faTag, 
  faCircleCheck,
  faUtensils,
  faUserCircle,
  faCalendarAlt,
  faTable,
  faClock,
  faIdCard,
  faMoneyBillWave
} from '@fortawesome/free-solid-svg-icons';
import generatePrintInvoiceHTML from '../../utils/printInvoice';
import { buildApiUrl } from '../../config/api.config';

const UserInvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInvoiceDetail = async () => {
    try {
      const response = await fetch(buildApiUrl(`invoice/user/${invoiceId}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': localStorage.getItem('token'),
        },
      });

      if (response.ok) {
        const data = await response.json();
        setInvoice(data);
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to synchronize bill details');
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      message.error('Failed to load transaction record');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceDetail();
  }, [invoiceId]);

  const goBack = () => {
    navigate(-1);
  };

  const printInvoice = () => {
    const printWindow = window.open('', '', 'height=800,width=1200');
    const invoiceHTML = generatePrintInvoiceHTML(invoice);
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="admin-layout" style={{ background: 'var(--tf-bg-dark)' }}>
        <main>
           <div className="tf-flex-center" style={{ padding: '10rem 2rem', flexDirection: 'column', gap: '1.5rem' }}>
              <div className="tf-spinner"></div>
              <p style={{ color: 'var(--tf-text-muted)', fontWeight: '500' }}>Retrieving your invoice details...</p>
           </div>
        </main>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="admin-layout" style={{ background: 'var(--tf-bg-dark)' }}>
        <main>
           <div className="tf-empty-state">
              <FontAwesomeIcon icon={faReceipt} size="3x" style={{ marginBottom: '1.5rem', opacity: 0.1 }} />
              <p>Invoice record not accessible or does not exist.</p>
              <button className="tf-btn tf-btn-primary" style={{ marginTop: '1rem' }} onClick={goBack}>Back to Invoices</button>
           </div>
        </main>
      </div>
    );
  }

  const isPaid = invoice.status === 'paid';

  return (
    <div className="admin-layout" style={{ background: 'var(--tf-bg-dark)', minHeight: '100vh' }}>
      <main>
        <div className="tf-card" style={{ marginBottom: '2rem' }}>
          <header style={{ marginBottom: '3.5rem' }}>
            <button 
              onClick={goBack} 
              className="tf-btn" 
              style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--tf-text-muted)', padding: '0.5rem 1rem', fontSize: '0.85rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Return to History
            </button>
            
            <div className="tf-flex" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1.5rem' }}>
              <div className="tf-flex" style={{ gap: '1rem', alignItems: 'center' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: 'linear-gradient(135deg, var(--tf-primary) 0%, #ff6b6b 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem' }}>
                  <FontAwesomeIcon icon={faReceipt} />
                </div>
                <div>
                  <h1 className="tf-heading-md" style={{ margin: 0, fontSize: '1.8rem' }}>Order Invoice #{invoice.invoiceNumber}</h1>
                  <p style={{ color: 'var(--tf-text-muted)', margin: 0 }}>Detailed breakdown of your transaction and billing summary</p>
                </div>
              </div>

              <button type="button" onClick={printInvoice} className="tf-btn tf-btn-primary" style={{ padding: '0.85rem 2.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '1rem' }}>
                <FontAwesomeIcon icon={faPrint} /> Print Invoice
              </button>
            </div>
          </header>

          <div className="tf-grid" style={{ gap: '2.5rem' }}>
            {/* Summary Cards */}
            <div className="tf-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                <div className="tf-card" style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.02)' }}>
                  <div style={{ color: 'var(--tf-text-muted)', fontSize: '0.75rem', fontWeight: '800', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Net Amount</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: '800' }}>₹{invoice.totalAmount.toFixed(2)}</div>
                </div>
                
                <div className="tf-card" style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.02)' }}>
                  <div style={{ color: 'var(--tf-error)', fontSize: '0.75rem', fontWeight: '800', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Applied Discount</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: '800', color: 'var(--tf-error)' }}>-₹{invoice.discount.toFixed(2)}</div>
                </div>

                <div className="tf-card" style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.02)' }}>
                  <div style={{ color: 'var(--tf-text-muted)', fontSize: '0.75rem', fontWeight: '800', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Total Taxes</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: '800' }}>₹{(invoice.cgst + invoice.sgst).toFixed(2)}</div>
                </div>

                <div className="tf-card" style={{ padding: '1.75rem 2rem', background: 'var(--tf-primary)', color: 'white', position: 'relative', overflow: 'hidden', boxShadow: '0 10px 30px rgba(255, 65, 53, 0.2)' }}>
                  <FontAwesomeIcon icon={isPaid ? faCircleCheck : faMoneyBillWave} style={{ position: 'absolute', right: '-15px', bottom: '-15px', fontSize: '6rem', opacity: 0.15 }} />
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem', fontWeight: '900', textTransform: 'uppercase', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', letterSpacing: '1px' }}>
                    {isPaid ? 'Payment Settled' : 'Payment Outstanding'}
                  </div>
                  <div style={{ fontSize: '2.2rem', fontWeight: '900' }}>
                    ₹{(invoice.finalAmount || invoice.totalAmount).toFixed(2)}
                  </div>
                </div>
            </div>

            {/* Tables Area */}
            <div className="tf-grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: '2.5rem', alignItems: 'flex-start' }}>
               <div className="tf-grid" style={{ gap: '2rem' }}>
                  <div className="tf-card" style={{ padding: '2rem' }}>
                    <h3 className="tf-text-accent" style={{ fontSize: '1.1rem', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem', borderLeft: '4px solid var(--tf-primary)', paddingLeft: '1rem' }}>
                        <FontAwesomeIcon icon={faUtensils} />
                        Itemized Particulars
                    </h3>
                    <div className="tf-list-container">
                      <div className="tf-list-header" style={{ gridTemplateColumns: '50px 2fr 1fr 100px 140px', gap: '1.5rem', padding: '1rem 1.5rem' }}>
                        <span>#</span>
                        <span>PRODUCT DESCRIPTION</span>
                        <span style={{ textAlign: 'right' }}>RATE</span>
                        <span style={{ textAlign: 'center' }}>QTY</span>
                        <span style={{ textAlign: 'right' }}>AMOUNT</span>
                      </div>
                      <div className="tf-grid" style={{ gap: '0' }}>
                        {invoice.foods.map((food, index) => (
                          <div key={index} className="tf-list-item" style={{ gridTemplateColumns: '50px 2fr 1fr 100px 140px', gap: '1.5rem', padding: '1.25rem 1.5rem' }}>
                            <div style={{ color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>{String(index + 1).padStart(2, '0')}</div>
                            <div style={{ fontWeight: '600' }}>{food.name}</div>
                            <div style={{ textAlign: 'right', color: 'var(--tf-text-muted)' }}>₹{(food.price || 0).toFixed(2)}</div>
                            <div style={{ textAlign: 'center', fontWeight: '700' }}>{food.quantity}</div>
                            <div style={{ textAlign: 'right', fontWeight: '700', color: 'var(--tf-primary)' }}>₹{(food.quantity * food.price).toFixed(2)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
               </div>

               {/* Info Sidepanel */}
               <div className="tf-grid" style={{ gap: '2rem' }}>
                  <div className="tf-card" style={{ padding: '1.5rem' }}>
                    <h4 style={{ fontSize: '0.85rem', color: 'var(--tf-primary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1.5rem', fontWeight: '800' }}>Reference Info</h4>
                    <div className="tf-grid" style={{ gap: '1.25rem' }}>
                       <div className="tf-flex" style={{ gap: '1rem' }}>
                          <FontAwesomeIcon icon={faUserCircle} style={{ color: 'var(--tf-text-muted)', opacity: 0.5 }} />
                          <div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--tf-text-muted)', textTransform: 'uppercase' }}>Customer</div>
                            <div style={{ fontWeight: '600' }}>{invoice.userId?.name || 'Guest'}</div>
                          </div>
                       </div>
                       <div className="tf-flex" style={{ gap: '1rem' }}>
                          <FontAwesomeIcon icon={faIdCard} style={{ color: 'var(--tf-text-muted)', opacity: 0.5 }} />
                          <div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--tf-text-muted)', textTransform: 'uppercase' }}>Invoice No.</div>
                            <div style={{ fontWeight: '600' }}>#{invoice.invoiceNumber}</div>
                          </div>
                       </div>
                       <div className="tf-flex" style={{ gap: '1rem' }}>
                          <FontAwesomeIcon icon={faCalendarAlt} style={{ color: 'var(--tf-text-muted)', opacity: 0.5 }} />
                          <div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--tf-text-muted)', textTransform: 'uppercase' }}>Date Issued</div>
                            <div style={{ fontWeight: '600' }}>{new Date(invoice.invoiceDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                          </div>
                       </div>
                    </div>
                  </div>

                  {invoice.reservedTableInfo && (
                    <div className="tf-card" style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--tf-border)' }}>
                       <h4 style={{ fontSize: '0.85rem', color: 'var(--tf-text-white)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1.5rem', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FontAwesomeIcon icon={faTable} size="xs" /> Reservation Details
                       </h4>
                       <div className="tf-grid" style={{ gap: '1.25rem' }}>
                          <div className="tf-flex" style={{ gap: '0.75rem' }}>
                             <span style={{ fontSize: '0.85rem', color: 'var(--tf-text-muted)' }}>Table:</span>
                             <span style={{ fontWeight: '700', color: 'var(--tf-primary)' }}>Unit #{invoice.reservedTableInfo.tableNumber}</span>
                          </div>
                          <div className="tf-flex" style={{ gap: '0.75rem' }}>
                             <span style={{ fontSize: '0.85rem', color: 'var(--tf-text-muted)' }}>Slot:</span>
                             <span style={{ fontWeight: '600' }}>{invoice.reservedTableInfo.slotTime}</span>
                          </div>
                          <div className="tf-flex" style={{ gap: '0.75rem' }}>
                             <span style={{ fontSize: '0.85rem', color: 'var(--tf-text-muted)' }}>Status:</span>
                             <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem', borderRadius: '4px', background: 'rgba(16, 185, 129, 0.1)', color: 'var(--tf-success)', fontWeight: '700' }}>VALID</span>
                          </div>
                          <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: 'var(--tf-text-muted)', fontStyle: 'italic', padding: '0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '6px' }}>
                             Note: ₹100 registration credit has been adjusted in the total.
                          </div>
                       </div>
                    </div>
                  )}
               </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserInvoiceDetail;
